export type Language = 'en' | 'nl' | 'fr' | 'de' | 'it' | 'es';
export type MeasurementSystem = 'metric' | 'imperial';
export type Theme = 'light' | 'dark' | 'auto';
export type TimeIn3DGraph = 'relative' | 'absolute';
export type ColorPalette = 'default' | 'colorblind_safe' | 'high_contrast';
export type StatusFilter = 'all' | 'live' | 'historical';
export type DateRange = '7d' | '30d' | '90d' | 'all';

export interface Temp3DRange {
  min: number;
  max: number;
}

export interface ProjectListSort {
  column: string;
  direction: 'asc' | 'desc';
}

// ── The saved grid view ─────────────────────────────────────────────────────
//
// The SAME shape at all three tiers, so the cascade merges like with like and a
// view saved at one tier can be promoted to another without translation. The
// server resolves and normalises the three tiers; the client renders the result.

export interface SavedProjectGridSort {
  columnId: string;
  direction: 'asc' | 'desc';
}

export interface SavedProjectGridFilter {
  op: 'contains' | 'equals' | 'in' | 'between';
  value?: string | null;
  values?: string[] | null;
  from?: string | null;
  to?: string | null;
}

export interface SavedProjectGridView {
  schemaVersion?: number;
  /** Order AND visibility in one list: a column is visible iff it appears here. */
  columns: string[];
  sort: SavedProjectGridSort[];
  groupBy: string[];
  filters: Record<string, SavedProjectGridFilter>;
  statusFilter: StatusFilter;
  rowsPerPage: number;
}

/** Which tier a save targets. Ordered least to most specific, as the cascade resolves. */
export type PreferenceTier = 'global' | 'company' | 'user';

// ── Effective preferences — the resolved values the UI reads ─────────────────

export interface EffectivePreferences {
  // Localization
  language: Language;
  measurementSystem: MeasurementSystem;
  timeFormat: string;
  dateFormat: string;
  // UI
  theme: Theme;
  rowsPerPage: number;
  // Charts
  /**
   * @deprecated REMOVED from Preferences — Joel decided not to build these.
   *
   * temp3DRange, timeIn3DGraph and chartsColorPalette were offered as settings
   * and read by nothing: no chart consulted any of them. They were UNWIRED rather
   * than superseded, so the choice was build the feature or drop the control, and
   * the ruling (2026-09-07) was drop. Controls, fields, payload and company-tier
   * defaults/locks are gone from both panes.
   *
   * Still on the wire type because the server stores and returns them. Delete the
   * group once it stops.
   */
  temp3DRange: Temp3DRange;
  timeIn3DGraph: TimeIn3DGraph;
  chartsColorPalette: ColorPalette;
  /**
   * @deprecated OBSOLETE — nothing reads this.
   *
   * It configured a chart poll interval. Live telemetry is PUSHED now
   * (LiveTelemetryService over the socket), so there is no poll for a user to
   * tune, and no code path consumed the value even before that. The control is
   * removed from Preferences; the field stays on the wire type only because the
   * server still stores and returns it. Delete once the server drops it.
   */
  chartsAutoRefreshIntervalS: number;
  /**
   * The RESOLVED grid view — global, then company, then user, then normalised by the
   * server. Already coherent, so the client renders it rather than re-deriving the
   * cascade; there is one implementation of the merge order and it is not this one.
   */
  projectGridView: SavedProjectGridView | null;
  /**
   * @deprecated SUPERSEDED by projectGridView, and no longer offered in Preferences.
   *
   * These five predate the saved grid view, which now carries columns, order,
   * sort and filters as one resolved object. Keeping both meant two sources for
   * the same intent with no rule about which wins — and nothing outside the
   * preferences screen ever read these, so the screen was collecting settings
   * that changed nothing.
   *
   * Still on the wire type because the server stores and returns them. Delete
   * the whole group once it stops.
   */
  projectListVisibleColumns: string[];
  projectListColumnOrder: string[] | null;
  projectListDefaultSort: ProjectListSort;
  projectListDefaultStatusFilter: StatusFilter;
  projectListDefaultDateRange: DateRange;
  // Branding
  brandLogoUrl: string | null;
  splashLogoUrl: string | null;
  reportLogoUrl: string | null;
  reportLogoWidthMm: number;
  reportLogoWidthPt: number;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  brandingDescription: string;
  // Exports
  wizardTemplateId: string | null;
  exportsDefaultTemplate: string;
  exportsDefaultPdfSections: string[];
  exportReportFooterText: string;
  // Fiber timer (legacy Desktop)
  fiberTimer: number;
  // Notifications
  notificationsEmailEnabled: boolean;
  notificationsRealtimeAlerts: boolean;
  notificationsTelemetryThreshold: number | null;
  notifyOnPreferenceOverwrite: boolean;
  notificationsNtfyTopic: string | null;
  notificationsNtfyEnabled: boolean;
  // Security
  securitySessionTimeoutMinutes: number;
  securityRequireMfa: boolean;
  securityAllowedAuthProviders: string[];
  // Cure sheets
  /** @deprecated Bare sheet ids. The server no longer reads this — see cureEnabledSheetVersions. */
  cureEnabledSheetIds: string[];
  /** Per-(sheet, version) allow-list: entries are "sheetId@version". */
  cureEnabledSheetVersions: string[];
  cureDefaultSheetId: string | null;
  // Subscriptions
  hybridStreamingEnabled: boolean;
  // Read-only metadata
  companyName: string | null;
  /** Field names locked at company level — inputs for these are disabled for users. */
  lockedFields: string[];
}

// ── Company preferences document (raw from backend) ─────────────────────────

export interface CompanyPreferences {
  /** The company's saved view, or null when this tier says nothing. */
  projectGridView: SavedProjectGridView | null;
  /** When set, the company's view is the last word and the user tier is skipped for it. */
  projectGridViewLocked: boolean;
  // Localization + locks
  defaultLanguage: Language;
  languageLocked: boolean;
  defaultMeasurementSystem: MeasurementSystem;
  measurementSystemLocked: boolean;
  timeFormat: string;
  timeFormatLocked: boolean;
  dateFormat: string;
  dateFormatLocked: boolean;
  // UI
  defaultTheme: Theme;
  // Charts + locks
  default3DTempMin: number;
  default3DTempMax: number;
  temp3DLocked: boolean;
  default3DTimeMode: TimeIn3DGraph;
  defaultChartsColorPalette: ColorPalette;
  chartsColorPaletteLocked: boolean;
  // Project list + locks
  projectListVisibleColumns: string[];
  projectListVisibleColumnsLocked: boolean;
  projectListDefaultSort: ProjectListSort;
  projectListDefaultSortLocked: boolean;
  // Branding
  brandLogoUrl: string | null;
  splashLogoUrl: string | null;
  reportLogoUrl: string | null;
  reportLogoWidthMm: number;
  reportLogoWidthPt: number;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  brandingDescription: string;
  // Exports
  defaultWizardTemplateId: string | null;
  exportReportFooterText: string;
  // Notifications + locks
  notificationsEmailEnabled: boolean;
  notificationsEmailLocked: boolean;
  notificationsRealtimeAlerts: boolean;
  notificationsRealtimeLocked: boolean;
  notificationsTelemetryThreshold: number | null;
  notificationsTelemetryLocked: boolean;
  // Security
  securitySessionTimeoutMinutes: number;
  securityRequireMfa: boolean;
  securityAllowedAuthProviders: string[];
  // Cure sheets + locks
  /** @deprecated Bare sheet ids. The server no longer reads this — see cureEnabledSheetVersions. */
  cureEnabledSheetIds: string[];
  /** Per-(sheet, version) allow-list: entries are "sheetId@version". */
  cureEnabledSheetVersions: string[];
  cureEnabledSheetsLocked: boolean;
  cureDefaultSheetId: string | null;
  cureDefaultSheetLocked: boolean;
  // Subscriptions
  hybridStreamingEnabled: boolean;
  // Legacy
  defaultFiberTimer: number;
}

// ── User preferences document (sparse overrides) ────────────────────────────

export interface UserPreferences {
  /** The user's own saved view — the tier that wins, unless the company locked it. */
  projectGridView: SavedProjectGridView | null;
  // Localization
  language: Language | null;
  measurementSystem: MeasurementSystem | null;
  /**
   * Day-first vs month-first. The user tier was missing this while carrying the
   * other two regional settings, so a user could not choose their own date order
   * even where the company had not locked it.
   */
  dateFormat: string | null;
  /**
   * 12-hour vs 24-hour. The third regional setting, and it travels with the other
   * two: a reader who wants DD/MM usually wants 24h, and the tier that lets them
   * choose one must let them choose the other.
   */
  timeFormat: string | null;
  // UI
  theme: Theme | null;
  rowsPerPage: number | null;
  // Charts
  temp3DRange: Temp3DRange | null;
  timeIn3DGraph: TimeIn3DGraph | null;
  chartsColorPalette: ColorPalette | null;
  chartsAutoRefreshIntervalS: number | null;
  // Project list
  projectListColumnOrder: string[] | null;
  projectListDefaultStatusFilter: StatusFilter | null;
  projectListDefaultDateRange: DateRange | null;
  // Exports
  wizardTemplateId: string | null;
  exportsDefaultTemplate: string | null;
  exportsDefaultPdfSections: string[] | null;
  // Fiber timer
  fiberTimer: number | null;
  // Notifications
  notificationsEmailEnabled: boolean | null;
  notificationsRealtimeAlerts: boolean | null;
  notifyOnPreferenceOverwrite: boolean | null;
  notificationsNtfyTopic: string | null;
  notificationsNtfyEnabled: boolean | null;
  // Cure sheets
  cureDefaultSheetId: string | null;
}

/** The global tier: product-wide defaults set by vortexadmins. */
export interface GlobalPreferences {
  projectGridView: SavedProjectGridView | null;
  /**
   * REGIONAL DEFAULTS at the product-wide tier. Null means "this tier says
   * nothing" and the built-in default applies — the same convention the company
   * and user tiers use, so the cascade treats all three alike.
   *
   * These are the floor of the cascade: global < company < user. Until now the
   * global tier carried only the grid view, so a product-wide regional default
   * could not be expressed at all and every company started from the hardcoded
   * client defaults.
   */
  language: Language | null;
  measurementSystem: MeasurementSystem | null;
  dateFormat: string | null;
  timeFormat: string | null;
  updatedBy?: string | null;
  updatedAt?: string | null;
}

export interface PreferencesApiResponse {
  global: GlobalPreferences | null;
  company: CompanyPreferences;
  user: UserPreferences;
  effective: EffectivePreferences;
  schemaVersion: number;
}

// ── Defaults ─────────────────────────────────────────────────────────────────

export const DEFAULT_EFFECTIVE_PREFERENCES: EffectivePreferences = {
  projectGridView: null,
  language: 'en',
  measurementSystem: 'metric',
  timeFormat: 'HH:mm:ss',
  dateFormat: 'yyyy-MM-dd',
  theme: 'dark',
  rowsPerPage: 25,
  temp3DRange: { min: -10, max: 180 },
  timeIn3DGraph: 'relative',
  chartsColorPalette: 'default',
  chartsAutoRefreshIntervalS: 30,
  projectListVisibleColumns: ['ProjectName', 'SerialNumber', 'LastTelemetryReceived', 'FiberType', 'CustomerName'],
  projectListColumnOrder: null,
  projectListDefaultSort: { column: 'LastTelemetryReceived', direction: 'desc' },
  projectListDefaultStatusFilter: 'all',
  projectListDefaultDateRange: 'all',
  brandLogoUrl: null,
  splashLogoUrl: null,
  reportLogoUrl: null,
  reportLogoWidthMm: 40,
  reportLogoWidthPt: 113,
  primaryColor: '#1a73e8',
  secondaryColor: '#5f6368',
  accentColor: '#34a853',
  brandingDescription: '',
  wizardTemplateId: null,
  exportsDefaultTemplate: 'Standard',
  exportsDefaultPdfSections: ['Curing', 'Zones', 'UserParameters', 'Graph3D', 'TempOverTime', 'Notes', 'Log'],
  exportReportFooterText: '',
  fiberTimer: 10,
  notificationsEmailEnabled: true,
  notificationsRealtimeAlerts: true,
  notificationsTelemetryThreshold: null,
  notifyOnPreferenceOverwrite: true,
  notificationsNtfyTopic: null,
  notificationsNtfyEnabled: false,
  securitySessionTimeoutMinutes: 60,
  securityRequireMfa: false,
  securityAllowedAuthProviders: ['msal', 'email'],
  cureEnabledSheetIds: [],
  cureEnabledSheetVersions: [],
  cureDefaultSheetId: null,
  hybridStreamingEnabled: true,
  companyName: null,
  lockedFields: [],
};

export const DEFAULT_COMPANY_PREFERENCES: CompanyPreferences = {
  projectGridView: null,
  projectGridViewLocked: false,
  defaultLanguage: 'en',
  languageLocked: false,
  defaultMeasurementSystem: 'metric',
  measurementSystemLocked: false,
  timeFormat: 'HH:mm:ss',
  timeFormatLocked: false,
  dateFormat: 'yyyy-MM-dd',
  dateFormatLocked: false,
  defaultTheme: 'dark',
  default3DTempMin: -10,
  default3DTempMax: 180,
  temp3DLocked: false,
  default3DTimeMode: 'relative',
  defaultChartsColorPalette: 'default',
  chartsColorPaletteLocked: false,
  projectListVisibleColumns: ['ProjectName', 'SerialNumber', 'LastTelemetryReceived', 'FiberType', 'CustomerName'],
  projectListVisibleColumnsLocked: false,
  projectListDefaultSort: { column: 'LastTelemetryReceived', direction: 'desc' },
  projectListDefaultSortLocked: false,
  brandLogoUrl: null,
  splashLogoUrl: null,
  reportLogoUrl: null,
  reportLogoWidthMm: 40,
  reportLogoWidthPt: 113,
  primaryColor: '#1a73e8',
  secondaryColor: '#5f6368',
  accentColor: '#34a853',
  brandingDescription: '',
  defaultWizardTemplateId: null,
  exportReportFooterText: '',
  notificationsEmailEnabled: true,
  notificationsEmailLocked: false,
  notificationsRealtimeAlerts: true,
  notificationsRealtimeLocked: false,
  notificationsTelemetryThreshold: null,
  notificationsTelemetryLocked: false,
  securitySessionTimeoutMinutes: 60,
  securityRequireMfa: false,
  securityAllowedAuthProviders: ['msal', 'email'],
  cureEnabledSheetIds: [],
  cureEnabledSheetVersions: [],
  cureEnabledSheetsLocked: false,
  cureDefaultSheetId: null,
  cureDefaultSheetLocked: false,
  hybridStreamingEnabled: true,
  defaultFiberTimer: 10,
};
